'use client'

import { forwardRef } from 'react'
import { cn } from '@/lib/utils'
import { Info } from 'lucide-react'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  hint?: string
  error?: string
  prefix?: React.ReactNode
  suffix?: React.ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, prefix, suffix, className, disabled, ...props }, ref) => (
    <div className={className}>
      {label && (
        <label className="text-[13px] font-medium mb-1.5 block" style={{ color: 'var(--text-primary)' }}>
          {label}
        </label>
      )}
      <div
        className="flex items-center h-9 rounded-lg overflow-hidden"
        style={{
          border: `1px solid ${error ? 'var(--fg-danger)' : 'var(--border-default)'}`,
          background: disabled ? 'var(--surface-tertiary)' : 'var(--surface-primary)',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {prefix && <div className="pl-3 flex items-center">{prefix}</div>}
        <input
          ref={ref}
          disabled={disabled}
          className="flex-1 h-full px-3 bg-transparent outline-none text-[13px]"
          style={{ color: 'var(--text-primary)' }}
          onFocus={(e) => {
            if (!error) {
              e.currentTarget.parentElement!.style.borderColor = 'var(--accent)'
              e.currentTarget.parentElement!.style.boxShadow = 'var(--shadow-focus)'
            }
          }}
          onBlur={(e) => {
            e.currentTarget.parentElement!.style.borderColor = error ? 'var(--fg-danger)' : 'var(--border-default)'
            e.currentTarget.parentElement!.style.boxShadow = 'none'
          }}
          {...props}
        />
        {suffix && <div className="pr-2 flex items-center">{suffix}</div>}
      </div>
      {hint && <p className="text-[12px] mt-1" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      {error && <p className="text-[12px] mt-1 flex items-center gap-1" style={{ color: 'var(--fg-danger)' }}><Info size={12} strokeWidth={2} />{error}</p>}
    </div>
  ),
)
Input.displayName = 'Input'

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  hint?: string
  maxLength?: number
  currentLength?: number
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, maxLength, currentLength = 0, className, ...props }, ref) => (
    <div className={className}>
      {label && (
        <label className="text-[13px] font-medium mb-1.5 block" style={{ color: 'var(--text-primary)' }}>
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        className="w-full rounded-lg px-3 py-2.5 text-[13px] outline-none resize-none leading-relaxed"
        style={{
          border: '1px solid var(--border-default)',
          background: 'var(--surface-primary)',
          color: 'var(--text-primary)',
        }}
        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = 'var(--shadow-focus)' }}
        onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.boxShadow = 'none' }}
        {...props}
      />
      {(hint || maxLength) && (
        <div className="flex justify-between mt-1.5">
          {hint && <span className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{hint}</span>}
          {maxLength && <span className="text-[12px] tabular-nums" style={{ color: 'var(--text-tertiary)' }}>{currentLength} / {maxLength}</span>}
        </div>
      )}
    </div>
  ),
)
Textarea.displayName = 'Textarea'

export interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

export function Toggle({ checked, onChange, disabled }: ToggleProps) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      className="w-9 h-5 rounded-full transition-colors duration-200 relative"
      style={{
        background: checked ? 'var(--accent)' : 'var(--border-emphasis)',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <div
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200"
        style={{
          transform: checked ? 'translateX(18px)' : 'translateX(2px)',
          boxShadow: 'var(--shadow-sm)',
        }}
      />
    </button>
  )
}
